import { CompositeLayer } from "@deck.gl/core";
import { PathStyleExtension } from "@deck.gl/extensions";
import {
  PathLayer as DeckPathLayer,
  type PathLayerProps as DeckPathLayerProps,
  LineLayer,
  type LineLayerProps,
  ScatterplotLayer,
  type ScatterplotLayerProps,
  TextLayer,
  type TextLayerProps,
} from "@deck.gl/layers";

import type { PathProblem } from "@/features/editor/types";
import {
  ArrowHeadLayer,
  type ArrowHeadLayerProps,
} from "@/features/map/layers/ArrowHeadLayer";
import type {
  PathDictLinkItemForVisualization,
  PathDictNodeItemForVisualization,
  PathsDataForVisualization,
} from "@/libs/models/path";

export const PATH_LAYER_ID = "PathLayer";

export const DEFAULT_Z_POSITION = 0.2;

// Path dimensions - in meters
export const PATH_LINE_SIZE = 0.5;
const PATH_LINE_FLOORCHANGE_SIZE = 0.15;
const PATH_LINE_SIZE_WITHTAGS = 1;
export const PATH_POINT_SIZE = 1.1;
const PATH_POINT_OUTLINE_SIZE = 0.7;

// Max/min pixels configuration - auto calculated using the base size and scale factor
const MAXPIXELS_BASE = 50;
const MINPIXELS_BASE = 6;

const PATH_LINE_MAXPIXELS = MAXPIXELS_BASE * PATH_LINE_SIZE; // Link line size uses the base value
const PATH_LINE_MINPIXELS = MINPIXELS_BASE * PATH_LINE_SIZE;

const PATH_LINE_FLOORCHANGE_MAXPIXELS =
  (PATH_LINE_FLOORCHANGE_SIZE / PATH_LINE_SIZE) * PATH_LINE_MAXPIXELS;
const PATH_LINE_FLOORCHANGE_MINPIXELS =
  (PATH_LINE_FLOORCHANGE_SIZE / PATH_LINE_SIZE) * PATH_LINE_MINPIXELS;

const PATH_POINT_MAXPIXELS =
  (PATH_POINT_SIZE / PATH_LINE_SIZE) * PATH_LINE_MAXPIXELS;
const PATH_POINT_MINPIXELS = 1;

const PATH_POINT_OUTLINE_MAXPIXELS =
  (PATH_POINT_OUTLINE_SIZE / PATH_LINE_SIZE) * PATH_LINE_MAXPIXELS;
const PATH_POINT_OUTLINE_MINPIXELS =
  (PATH_POINT_OUTLINE_SIZE / PATH_LINE_SIZE) * PATH_LINE_MINPIXELS;

// Path colors
export const PATH_COLOR = new Uint8Array([30, 144, 255, 200]); // DodgerBlue
const PATH_COLOR_SELECTED = new Uint8Array([255, 215, 0, 255]); // Gold
const PATH_COLOR_DIRECTIONAL = PATH_COLOR;
const PATH_COLOR_FLOORCHANGE = new Uint8Array([225, 14, 166, 255]); // Hollywoodcerise
export const PATH_TMP_LINK_COLOR = new Uint8Array([69, 82, 97, 150]); // Charcoal
const PATH_NODE_OUTLINE_COLOR = new Uint8Array([
  PATH_COLOR[0],
  PATH_COLOR[1],
  PATH_COLOR[2],
  100,
]);
const PATH_NODE_OUTLINE_COLOR_SELECTED = new Uint8Array([255, 215, 0, 255]); // Gold
const PATH_NODE_FLOORCHANGE_TO_COLOR = new Uint8Array([105, 105, 105, 200]);
const INCLUDED_TAGS_COLOR = new Uint8Array([11, 218, 81, 220]); // Malachite
const EXCLUDED_TAGS_COLOR = new Uint8Array([220, 20, 60, 220]); // Crimson
const PATH_PROBLEM_INNER_COLOR = new Uint8Array([255, 99, 71, 125]); // Tomato
const PATH_PROBLEM_BORDER_COLOR = new Uint8Array([255, 99, 71, 255]); // Tomato

// DeckGL dashed path extension
const pathStyleExtension = new PathStyleExtension({ dash: true });

/**
 * Get color for a link based on its tag type and selected feature ID.
 *
 * @param link - Link object
 * @param selectedFeatureId - The ID of the selected feature, if any.
 */
const getColorFromLink = (
  link: PathDictLinkItemForVisualization,
  selectedFeatureId?: string,
  isFloorChange?: boolean,
) => {
  if (link.id === selectedFeatureId) return PATH_COLOR_SELECTED;
  if (isFloorChange === true) return PATH_COLOR_FLOORCHANGE;
  if (link.tagType === "included") return INCLUDED_TAGS_COLOR;
  if (link.tagType === "excluded") return EXCLUDED_TAGS_COLOR;
  if (link.origin !== "both") return PATH_COLOR_DIRECTIONAL;
  return PATH_COLOR;
};

const getSizeFromLink = (link: PathDictLinkItemForVisualization) => {
  if (link.tagType === "included") return PATH_LINE_SIZE_WITHTAGS;
  if (link.tagType === "excluded") return PATH_LINE_SIZE_WITHTAGS;
  return PATH_LINE_SIZE;
};

interface Props {
  data: PathsDataForVisualization;
  fontFamily: string | null;
  beforeId: string;
  displayNodeIds: boolean | null;
  selectedFeatureId: string | null;
  pathProblems: PathProblem[] | null;
}

export default class PathLayer extends CompositeLayer<Props> {
  componentName = "PathLayer";

  constructor(props: any) {
    props = { id: PATH_LAYER_ID, z_index: 0.5, ...props };
    super(props);
  }

  renderLayers() {
    const { data, displayNodeIds, selectedFeatureId, pathProblems } =
      this.props;
    const {
      normalNodes,
      normalLinks,
      notAccessibleLinks,
      floorChangeFromNodes,
      floorChangeLinks,
      floorChangeToNodes,
    } = data;

    return [
      // Layer for path problem nodes:
      new ScatterplotLayer(
        this.getSubLayerProps({
          billboard: false,
          data: pathProblems,
          getFillColor: () => PATH_PROBLEM_INNER_COLOR,
          getLineColor: () => PATH_PROBLEM_BORDER_COLOR,
          getPosition: (d) => [d.lng, d.lat, d.z ?? DEFAULT_Z_POSITION],
          getRadius: 1,
          id: "path-problems-nodes-layer",
          lineWidthMaxPixels: 4,
          lineWidthMinPixels: 4,
          lineWidthScale: 1,
          pickable: false,
          radiusMinPixels: 80,
          radiusUnits: "meters",
          stroked: true,
        } as ScatterplotLayerProps<PathDictNodeItemForVisualization>),
      ),

      // Main links layer (all links)
      new LineLayer(
        this.getSubLayerProps({
          data: normalLinks ?? [],
          getColor: (d) => getColorFromLink(d, selectedFeatureId),
          getSourcePosition: (d) => [
            d.from.lng,
            d.from.lat,
            d.from.z ?? DEFAULT_Z_POSITION,
          ],
          getTargetPosition: (d) => [
            d.to.lng,
            d.to.lat,
            d.to.z ?? DEFAULT_Z_POSITION,
          ],
          getWidth: getSizeFromLink,
          id: "path-normal-links-layer",
          pickable: true,
          updateTriggers: {
            getColor: selectedFeatureId,
          },
          widthMaxPixels: PATH_LINE_MAXPIXELS,
          widthMinPixels: PATH_LINE_MINPIXELS,
          widthUnits: "meters",
        } as LineLayerProps<PathDictLinkItemForVisualization>),
      ),

      // Arrowheads layer - normal links
      new ArrowHeadLayer(
        this.getSubLayerProps({
          data: [...normalLinks, ...notAccessibleLinks].filter(
            (d) => d.origin && d.origin !== "both",
          ),
          getArrowDirection: (d) => {
            // Will never be "both", as these are always links that have their
            // direction set to either source or target
            return d.origin === "target" ? "target" : "source";
          },
          getFillColor: (d) => getColorFromLink(d, selectedFeatureId),
          getSourcePosition: (d) => [
            d.from.lng,
            d.from.lat,
            d.from.z ?? DEFAULT_Z_POSITION,
          ],
          getTargetPosition: (d) => [
            d.to.lng,
            d.to.lat,
            d.to.z ?? DEFAULT_Z_POSITION,
          ],
          id: "normal-links-arrowlayer",
        } as ArrowHeadLayerProps<PathDictLinkItemForVisualization>),
      ),

      // Not accesible links layer
      new DeckPathLayer(
        this.getSubLayerProps({
          billboard: false,
          capRounded: false,
          dashGapPickable: true,
          dashJustified: true,
          data: notAccessibleLinks,
          extensions: [pathStyleExtension],
          getColor: (d) => getColorFromLink(d, selectedFeatureId),
          getDashArray: [1, 1],
          getPath: (d) => [
            [d.from.lng, d.from.lat, d.from.z ?? DEFAULT_Z_POSITION],
            [d.to.lng, d.to.lat, d.to.z ?? DEFAULT_Z_POSITION],
          ],
          getWidth: getSizeFromLink,
          id: "path-not-accesible-links-layer",
          pickable: true,
          updateTriggers: {
            getColor: selectedFeatureId,
          },
          widthMaxPixels: PATH_LINE_MAXPIXELS,
          widthMinPixels: PATH_LINE_MINPIXELS,
          widthUnits: "meters",
        } as DeckPathLayerProps<PathDictLinkItemForVisualization>),
      ),

      // Arrowheads layer - floor change links
      new ArrowHeadLayer(
        this.getSubLayerProps({
          data: floorChangeLinks.filter((d) => d.origin && d.origin !== "both"),
          getArrowDirection: (d) => {
            // Will never be "both", as these are always links that have their
            // direction set to either source or target
            return d.origin === "target" ? "target" : "source";
          },
          getFillColor: (d) => getColorFromLink(d, selectedFeatureId, true),
          getSourcePosition: (d) => [
            d.from.lng,
            d.from.lat,
            d.from.z ?? DEFAULT_Z_POSITION,
          ],
          getTargetPosition: (d) => [
            d.to.lng,
            d.to.lat,
            d.to.z ?? DEFAULT_Z_POSITION,
          ],
          id: "floorchange-links-arrowlayer",
        } as ArrowHeadLayerProps<PathDictLinkItemForVisualization>),
      ),

      // Floor change links
      new DeckPathLayer(
        this.getSubLayerProps({
          billboard: true,
          capRounded: true,
          dashGapPickable: true,
          dashJustified: true,
          data: floorChangeLinks,
          extensions: [pathStyleExtension],
          getColor: (d) => getColorFromLink(d, selectedFeatureId, true),
          getDashArray: [2, 3],
          getPath: (d) => [
            [d.from.lng, d.from.lat, d.from.z ?? DEFAULT_Z_POSITION],
            [d.to.lng, d.to.lat, d.to.z ?? DEFAULT_Z_POSITION],
          ],
          getWidth: PATH_LINE_FLOORCHANGE_SIZE,
          id: "path-floorchange-links-layer",
          pickable: true,
          updateTriggers: {
            getColor: selectedFeatureId,
          },
          widthMaxPixels: PATH_LINE_FLOORCHANGE_MAXPIXELS,
          widthMinPixels: PATH_LINE_FLOORCHANGE_MINPIXELS,
          widthUnits: "meters",
        } as DeckPathLayerProps<PathDictLinkItemForVisualization>),
      ),

      // Nodes layer:
      new ScatterplotLayer(
        this.getSubLayerProps({
          data: normalNodes ?? [],
          getFillColor: (d) =>
            d.id == selectedFeatureId ? PATH_COLOR_SELECTED : PATH_COLOR,
          getLineColor: (d) =>
            d.id == selectedFeatureId
              ? PATH_NODE_OUTLINE_COLOR_SELECTED
              : PATH_NODE_OUTLINE_COLOR,
          getPosition: (d) => [d.lng, d.lat, d.z ?? DEFAULT_Z_POSITION],
          getRadius: PATH_POINT_SIZE,
          id: "path-normal-nodes-layer",
          lineWidthMaxPixels: PATH_POINT_OUTLINE_MAXPIXELS,
          lineWidthMinPixels: PATH_POINT_OUTLINE_MINPIXELS,
          lineWidthScale: PATH_POINT_OUTLINE_SIZE,
          radiusMaxPixels: PATH_POINT_MAXPIXELS,
          radiusMinPixels: PATH_POINT_MINPIXELS,
          stroked: true,
          updateTriggers: {
            getFillColor: selectedFeatureId,
            getLineColor: selectedFeatureId,
          },
          widthUnits: "meters",
        } as ScatterplotLayerProps<PathDictNodeItemForVisualization>),
      ),

      // Floor change destination node
      new ScatterplotLayer(
        this.getSubLayerProps({
          data: floorChangeToNodes ?? [],
          getFillColor: (d) =>
            d.id == selectedFeatureId
              ? PATH_COLOR_SELECTED
              : PATH_NODE_FLOORCHANGE_TO_COLOR,
          getPosition: (d) => [d.lng, d.lat, d.z ?? DEFAULT_Z_POSITION],
          getRadius: PATH_POINT_SIZE / 2,
          id: "path-nodes-floorchange-destination-layer",
          radiusMaxPixels: PATH_POINT_MAXPIXELS,
          radiusMinPixels: PATH_POINT_MINPIXELS,
          updateTriggers: {
            getFillColor: selectedFeatureId,
            getLineColor: selectedFeatureId,
          },
          widthUnits: "meters",
        } as ScatterplotLayerProps<PathDictNodeItemForVisualization>),
      ),

      // Floor change origin node
      new ScatterplotLayer<PathProblem>(
        this.getSubLayerProps({
          data: floorChangeFromNodes ?? [],
          getFillColor: (d) =>
            d.id == selectedFeatureId ? PATH_COLOR_SELECTED : PATH_COLOR,
          getLineColor: (d) =>
            d.id == selectedFeatureId
              ? PATH_NODE_OUTLINE_COLOR_SELECTED
              : PATH_NODE_OUTLINE_COLOR,
          getPosition: (d) => [d.lng, d.lat, d.z ?? DEFAULT_Z_POSITION],
          getRadius: PATH_POINT_SIZE,
          id: "path-nodes-floorchange-origin-layer",
          lineWidthMaxPixels: PATH_POINT_OUTLINE_MAXPIXELS,
          lineWidthMinPixels: PATH_POINT_OUTLINE_MINPIXELS,
          lineWidthScale: PATH_POINT_OUTLINE_SIZE,
          radiusMaxPixels: PATH_POINT_MAXPIXELS,
          radiusMinPixels: PATH_POINT_MINPIXELS,
          stroked: true,
          updateTriggers: {
            getFillColor: selectedFeatureId,
            getLineColor: selectedFeatureId,
          },
          widthUnits: "meters",
        } as ScatterplotLayerProps<PathDictNodeItemForVisualization>),
      ),

      // Layer for problem labels:
      new TextLayer(
        this.getSubLayerProps({
          data: this.props.pathProblems,
          fontFamily: this.props.fontFamily || "Arial, sans-serif",
          fontWeight: "bold",
          getAlignmentBaseline: "top",
          getColor: [50, 50, 50],
          getPosition: (d) => [d.lng, d.lat, DEFAULT_Z_POSITION],
          getSize: 1,
          getText: (d) => {
            if (d.problemType === "outside") return "NODE\nOUTSIDE";
            if (d.problemType === "isolated") return "ISOLATED\nNODE";
            if (d.problemType === "near") return "CLOSE\nNODES";
            return "";
          },
          id: "paths-problem-label",
          parameters: { depthTest: false },
          pickable: false,
          sizeMinPixels: 20,
          sizeUnits: "meters",
        } as TextLayerProps<PathProblem>),
      ),

      // Node labels:
      new TextLayer(
        this.getSubLayerProps({
          data: [...normalNodes, ...floorChangeFromNodes],
          fontFamily: this.props.fontFamily || "Helvetica, sans-serif",
          fontSettings: { sdf: true },
          fontWeight: "bold",
          getAlignmentBaseline: "bottom",
          getColor: [50, 50, 50],
          getPosition: (d) => [d.lng, d.lat, d.z ?? DEFAULT_Z_POSITION],
          getSize: 1.4,
          getText: (d) => d.id.toString(),
          id: "paths-node-id",
          outlineColor: [255, 255, 255, 200],
          outlineWidth: 2,
          pickable: false,
          sizeMaxPixels: 20,
          sizeMinPixels: 12,
          sizeUnits: "meters",
          visible: displayNodeIds,
        } as TextLayerProps<PathDictNodeItemForVisualization>),
      ),
    ];
  }
}
